import * as React from "react";
import * as Constants from "@/shared/constants";
import type {FixedBreak} from "@/shared/timr";
import {TimeInput} from "@/shared/ui/primitives/TimeInput";
import {
	FIXED_BREAK_FIELDS,
	hasFixedBreakRangeError,
	type FixedBreakConfiguration,
	type FixedBreakField,
	type FixedBreakKey
} from "@/features/admin/breakModel/fixedBreakConfigurationModel";

interface FixedBreakPerDayConfigurationProps {
	configuration: FixedBreakConfiguration;
	errorFields: Set<string>;
	onChange: (fixedBreakKey: FixedBreakKey, field: FixedBreakField, value: string | null) => void;
}

interface FixedBreakPerDayTimeRangeProps {
	fixedBreakKey: FixedBreakKey;
	index: number;
	fromField: FixedBreakField;
	toField: FixedBreakField;
	fixedBreak: FixedBreak;
	errorFields: Set<string>;
	onChange: (fixedBreakKey: FixedBreakKey, field: FixedBreakField, value: string | null) => void;
}

/**
 * Renders the dense seven-column editor for per-day fixed breaks.
 */
export function FixedBreakPerDayConfiguration({configuration, errorFields, onChange}: Readonly<FixedBreakPerDayConfigurationProps>) {
	return (
		<div className="fixed-break-day-columns">
			{Constants.WEEKDAYS.map(day => {
				const fixedBreakKey: FixedBreakKey = `fixedBreak${day}`;
				return (
					<div key={day} className="fixed-break-day-card">
						<div className="fixed-break-day-title">{messages[`admin.clientSettings.weekday.${day.toUpperCase()}`]}</div>
						{FIXED_BREAK_FIELDS.map(([fromField, toField], index) => (
							<FixedBreakPerDayTimeRange
								key={`${fromField}-${toField}`}
								fixedBreakKey={fixedBreakKey}
								index={index}
								fromField={fromField}
								toField={toField}
								fixedBreak={configuration[fixedBreakKey]}
								errorFields={errorFields}
								onChange={onChange}
							/>
						))}
					</div>
				);
			})}
		</div>
	);
}

/**
 * Renders one compact per-day fixed-break range.
 *
 * Per-day mode shows all weekday ranges in a dense grid, so this component omits the
 * enable checkbox and lets empty time inputs represent unused ranges.
 */
function FixedBreakPerDayTimeRange({
	fixedBreakKey,
	index,
	fromField,
	toField,
	fixedBreak,
	errorFields,
	onChange
}: Readonly<FixedBreakPerDayTimeRangeProps>) {
	const hasError = hasFixedBreakRangeError(errorFields, fixedBreakKey, fromField, toField);
	return (
		<div className={`fixed-break-time-range${hasError ? " has-error" : ""}`}>
			<TimeInput
				name={`${fixedBreakKey}.${fromField}`}
				value={fixedBreak[fromField]}
				ariaLabel={`${(messages.withArguments("breakModel.fixedBreak.breakNumber", index + 1))} ${messages["breakModel.automaticBreaks.fixedBreakFrom"]}`}
				className="fixed-break-time-input"
				onValueChange={value => onChange(fixedBreakKey, fromField, value)}
			/>
			<span className="fixed-break-time-separator">-</span>
			<TimeInput
				name={`${fixedBreakKey}.${toField}`}
				value={fixedBreak[toField]}
				ariaLabel={`${(messages.withArguments("breakModel.fixedBreak.breakNumber", index + 1))} ${messages["breakModel.automaticBreaks.fixedBreakTo"]}`}
				className="fixed-break-time-input"
				onValueChange={value => onChange(fixedBreakKey, toField, value)}
			/>
		</div>
	);
}
